import { WorkflowData } from "@/contexts/WorkflowContext";

/**
 * Standardizes raw workflow data or a finalized analysis into 
 * a polymorphic Template Configuration.
 */
export const buildTemplateConfig = (source: any) => {
  // If writeupType is an object (like { id: '...', label: '...' }), extract just the ID string
  const rawWriteup = source.writeupType || source.summary?.writeupType || 'full_analysis';
  const writeupType = typeof rawWriteup === 'object' ? rawWriteup.value || rawWriteup.id : rawWriteup;

  const summary = source.summaryPreferences || source.summary?.summaryPreferences || {};
  const rawData = source.rawData || source;

  const toArray = (data: any) => {
    if (data instanceof Set) return Array.from(data);
    return Array.isArray(data) ? data : [];
  };

  // Zod expects an array [{}], so we ensure it's always an array
  let obsRecords = source.offBalanceSheetRecords || rawData.offBalanceSheet || [];
  if (obsRecords && !Array.isArray(obsRecords)) {
    obsRecords = [obsRecords]; // Wrap in array if it's a single object
  }

  return {
    global: {
      writeupType: String(writeupType), // Ensure it's a string
      summaryStyle: summary.summaryStyle || 'comprehensive',
      summaryLength: summary.summaryLength || 'standard',
      customInstructions: summary.customInstructions || '',
    },
    dataPoints: {
      financialStatements: toArray(summary.selectedDataPoints?.financialStatements),
      creditReport: toArray(summary.selectedDataPoints?.creditReport),
      metrics: toArray(summary.selectedDataPoints?.metrics),
      corporateSearch: toArray(summary.selectedDataPoints?.corporateSearch),
      bankStatements: toArray(summary.selectedDataPoints?.bankStatements),
    },
    modules: {
      financial: {
        enabled: !!(source.financialData || source.financialUploadId),
        statementType: summary.financialStatementType || 'interim_fs',
        selectedMetricIds: Array.isArray(source.selectedMetrics)
          ? source.selectedMetrics
          : (rawData.selectedMetrics || []),
        customRatios: Array.isArray(rawData.customRatios) ? rawData.customRatios : [],
        offBalanceSheet: obsRecords, // Now guaranteed to be an array
      },
      creditReport: {
        enabled: !!(source.creditReportUploadId || source.creditReportData),
        inclusionMode: source.creditReportInclusionMode || 'automatic',
        selectedKeys: source.selectedCreditReportItems || {}
      },
      bank: {
        enabled: !!(source.bankStatementUploadId || source.bankStatementFindings),
        type: summary.bankStatementType || 'business',
      },
      corporate: {
        enabled: !!(source.corporateUploadId || source.accountData),
      }
    }
  };
};

export const applyTemplateToWorkflow = (config: any, currentData: WorkflowData): Partial<WorkflowData> => {
  const global = config.global || {};
  const modules = config.modules || {};

  // If the template was created from the API dashboard it won't have dataPoints
  // but it will have modules.*.enabled flags.  Derive sentinel dataPoints from
  // the enabled modules so the workflow step-visibility logic works correctly.
  const dataPoints = config.dataPoints && Object.values(config.dataPoints).some((v: any) => Array.isArray(v) && v.length > 0)
    ? config.dataPoints
    : {
        financialStatements: modules.financial?.enabled ? ['__enabled__'] : [],
        creditReport: modules.creditReport?.enabled ? ['__enabled__'] : [],
        metrics: modules.financial?.enabled ? ['__enabled__'] : [],
        corporateSearch: modules.corporate?.enabled ? ['__enabled__'] : [],
        bankStatements: modules.bank?.enabled ? ['__enabled__'] : [],
      };

  // Ensure offBalanceSheet is an OBJECT for the frontend/API
  let obs = modules.financial?.offBalanceSheet || {};
  if (Array.isArray(obs)) {
    // If it's an array like [{}], take the first object or default to {}
    obs = obs[0] || {};
  }

  const updatedData: Partial<WorkflowData> = {
    writeupType: global.writeupType || 'full_analysis',
    summaryPreferences: {
      selectedDataPoints: {
        financialStatements: dataPoints.financialStatements || [],
        creditReport: dataPoints.creditReport || [],
        metrics: dataPoints.metrics || [],
        corporateSearch: dataPoints.corporateSearch || [],
        bankStatements: dataPoints.bankStatements || [],
      },
      createdAt: currentData.summaryPreferences?.createdAt || new Date().toISOString(),
      summaryStyle: global.summaryStyle || 'comprehensive',
      summaryLength: global.summaryLength || 'standard',
      customInstructions: global.customInstructions || '',
      bankStatementType: modules.bank?.type || 'business',
      financialStatementType: modules.financial?.statementType || 'interim_fs',
      enableExternalResearch: currentData.summaryPreferences?.enableExternalResearch ?? true,
    },

    selectedMetrics: modules.financial?.selectedMetricIds || [],
    customRatios: modules.financial?.customRatios || [],

    offBalanceSheetRecords: obs,

    selectedCreditReportItems: {
      personalInfo: [],
      creditSummary: [],
      accountHistory: [],
      inquiries: [],
      publicRecords: [],
      // Merge any specific keys from the template on top of the defaults
      // so that every expected property always exists (even if selectedKeys
      // is an empty object {} from the API dashboard template creator).
      ...(modules.creditReport?.selectedKeys || {}),
    },
    creditReportInclusionMode: modules.creditReport?.inclusionMode || 'automatic',
  };

  return updatedData;
};